import { TextInput } from "flowbite-react";
import {
  forwardRef,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useTransition,
} from "react";
import * as actions from "../../../actions";
import { useDispatch, useSelector } from "react-redux";
import { getParam, isValidParams } from "../../../util/helper";
const accessNested = (obj, key) =>
  key
    .split(".")
    .reduce(
      (_obj, _key) => (_obj && typeof _obj === "object" ? _obj[_key] : null),
      obj
    );

function SearchDropdown(
  {
    name,
    label,
    options,
    optionId = "key",
    optionName = "value",
    formatDisplayName,
    formik,
    value,
    className,
    disabled,
    showNull = true,
    nullLabel,
    readOnly,
    showError = true,
    id = "",
    onClose,
    onSearch,
    onSelect,
    optionPrefix,
    searchplaceholder,
    remote,
    required,
    debounce,
    dataSource
  },
  ref
) {
  const dispatch = useDispatch();
  const formikValue = formik ? accessNested(formik.values, name || id) : value;
  const formikError = formik ? accessNested(formik.errors, name || id) : "";
  const formikTouched = formik
    ? accessNested(formik.touched, name || id)
    : false;
  const _required = formik ? formik.isFieldRequired(name || id) : required;
  const [_options, setOptions] = useState(options || []);
  // If an attribute needs data from the different source like api, the api response is stored here
  // Eg: Dropdown options can be fetched dynamically using the api, response is stored here
  const sourceData = useSelector(state => {
    if (!dataSource?.source) {
      return null;
    } else {
      return accessNested(state, dataSource?.source);
    }
  });

  const store = useSelector(state => state);

  useEffect(() => {
    if (options?.length > 0 && typeof options[0] !== "object") {
      setOptions(
        options.map((_option) => ({
          [optionId]: _option,
          [optionName]: _option,
        }))
      );
    } else {
      setOptions(options || []);
    }
  }, [options]);

  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState();
  const [dropdownWidth, setDropdownWidth] = useState(null);
  const [positionBelow, setPositionBelow] = useState(true);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [keyword, setKeyword] = useState("");
  const isDataSourceTriggered = useRef(false)
  const [isPending, startTransition] = useTransition();

  const buttonRef = useRef();
  const DATA_SOURCES = {
    FORMIK: 'FORMIK',
    DROPDOWN_SELECTION: 'DROPDOWN_SELECTION'
  }
  const searchRef = useRef();
  const dropdownRef = useRef();
  const selectedRef = useRef();
  // Contains the last changed field either formik or chip list selection
  const changeSourceRef = useRef(null);
  // console.log('options', options)
  let timerRef = useRef();
  useEffect(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    if (debounce)
      timerRef.current = setTimeout(
        () => {
          onSearch && onSearch(keyword);
        },
        typeof debounce === "boolean" ? 1000 : debounce
      );
    else onSearch && onSearch(keyword);
  }, [keyword]);

  useLayoutEffect(() => {
    const calculatePosition = () => {
      if (buttonRef.current) {
        const buttonRect = buttonRef.current.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        isOpen && setPositionBelow(buttonRect.bottom + 260 <= windowHeight);
        setDropdownWidth(buttonRect.width);
      }
      if (ref?.current) {
        const buttonRect = ref.current.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        isOpen && setPositionBelow(buttonRect.bottom + 260 <= windowHeight);
        setDropdownWidth(buttonRect.width);
      }
      if (searchRef.current) {
        const buttonRect = searchRef.current.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        isOpen && setPositionBelow(buttonRect.bottom + 260 <= windowHeight);
        // dropdownWidth === 0 &&
        setDropdownWidth(buttonRect.width);
      }
    };

    const handleScroll = () => {
      calculatePosition();
    };

    calculatePosition();

    window.addEventListener("scroll", handleScroll);

    if (selectedRef.current && dropdownRef.current) {
      const children = dropdownRef.current.children;
      for (let i = 0; i < children.length; i++) {
        if (children[i] === selectedRef.current) {
          setFocusedIndex(i);
        }
      }
    }

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      onClose && onClose(selectedOption);
      setFocusedIndex(-1);
      setKeyword("");
    }
  }, [isOpen]);

  useEffect(() => {
    let filteredOptions = _options?.filter((option) =>
      remote
        ? true
        : !keyword
          ? true
          : option[optionName]?.toString().toLowerCase().includes(keyword) ||
            option[optionId]
              .toString()
              .toLowerCase()
              .includes(keyword.toLowerCase())
    );
    if (isOpen && focusedIndex >= 0 && filteredOptions.length > 0) {
      dropdownRef.current.children?.[focusedIndex]?.scrollIntoView({
        block: "nearest",
      });
    }
  }, [focusedIndex, isOpen, _options]);

  useEffect(() => {
    if (selectedOption?.[optionId] !== formikValue) {
      changeSourceRef.current = DATA_SOURCES.DROPDOWN_SELECTION;
    }
  }, [selectedOption]);

  useEffect(() => {
    if (formikValue !== selectedOption?.[optionId] ) {
      changeSourceRef.current = DATA_SOURCES.FORMIK;
    }
  }, [formikValue]);

  
  // This useEffect can only be declared after the `changeSourceRef` is changed.
  useEffect(() => {
    if (typeof _options?.[0] !== 'object') return;
    if (changeSourceRef.current === DATA_SOURCES.FORMIK) {
      // Update selected when formikValue changes
      setSelectedOption(
        _options?.filter((v) => v[optionId] === formikValue)[0]
      );
      changeSourceRef.current = null; // Reset after sync
    } else if (changeSourceRef.current === DATA_SOURCES.DROPDOWN_SELECTION) {
      // Update formik when selected changes
      formik.setFieldValue(
        name || id,
        selectedOption
          ? selectedOption[optionId]
          : showNull
            ? null
            : formikValue
      );
      if (!selectedOption) setKeyword("");
      changeSourceRef.current = null; // Reset after sync
    }
  }, [formikValue, selectedOption, _options]);


  useEffect(() => {
    if (isDataSourceTriggered.current || sourceData?.length || !store || typeof dataSource?.action != 'string') return;

    switch (dataSource.type) {
      case 'api':
        if (typeof actions[dataSource.action] === 'function') {
          const params = [];

          // Params for stores
          if (dataSource.actionParams) {
            params.push(...getParam(dataSource.actionParams, store));
          }
          if ((dataSource.actionParams && isValidParams(params)) || !dataSource.actionParams) {
            isDataSourceTriggered.current = true;
            dispatch(actions[dataSource.action](...params));
          };
        } else {
          console.error('Data source action not available', dataSource.action);
        }
    }
  }, [dataSource, store]);

  useEffect(() => {
    if (sourceData?.length) {
      switch (dataSource.target) {
        case 'options':
          const fetched_options = sourceData.map((option => {
            return {
              key: option[dataSource.key],
              value: option[dataSource.value],
            };
          }));
          return setOptions(fetched_options);
      }
    }
  }, [sourceData])

  const toggleDropdown = () => {
    !readOnly && !disabled && setIsOpen(!isOpen);
  };

  const handleOptionClick = (option, target) => {
    setSelectedOption(option);
    setIsOpen(false);
    formik && formik.setFieldTouched(name || id, true);
    onSelect && onSelect(option);
  };

  const handleKeyDown = (e) => {
    let filteredOptions = _options?.filter((option) =>
      remote
        ? true
        : !keyword
          ? true
          : option[optionName]?.toString().toLowerCase().includes(keyword) ||
            option[optionId]
              .toString()
              .toLowerCase()
              .includes(keyword.toLowerCase())
    );
    if (e.key === "Enter") {
      e.preventDefault();
      if (focusedIndex >= 0) {
        console.log(focusedIndex);
        if (showNull && focusedIndex === 0) handleOptionClick();
        else
          handleOptionClick(
            filteredOptions[showNull ? focusedIndex - 1 : focusedIndex]
          );
        setFocusedIndex(0);
      }
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setFocusedIndex((prevIndex) => {
        if (showNull) {
          return prevIndex + 1 >= filteredOptions.length
            ? filteredOptions.length
            : prevIndex + 1;
        } else {
          return prevIndex + 1 >= filteredOptions.length
            ? filteredOptions.length - 1
            : prevIndex + 1;
        }
      });
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setFocusedIndex((prevIndex) => {
        return prevIndex - 1 < 0 ? 0 : prevIndex - 1;
      });
    } else if (e.key === "Escape") {
      setIsOpen();
    }
  };

  return (
    <div className={className + " relative inline-block text-primary"}>
      {label && (
        <label
          className={`text-sm font-medium text-gray-900 dark:text-gray-300 ${
            _required && !readOnly
              ? "after:content-['_*'] after:text-red-500"
              : ""
          }`}
          htmlFor={id}
        >
          {label}
        </label>
      )}
      {isOpen && (
        <div
          className="fixed inset-0 opacity-25 z-40"
          onClick={() => setIsOpen(false)} // Clicking on the backdrop closes the dropdown
        ></div>
      )}
      <div id="trigger" className="mt-2">
        {isOpen ? (
          <TextInput
            type="text"
            className="relative [&_input]:sticky [&_input]:z-40 [&_input]:!bg-transparent w-full"
            ref={searchRef}
            placeholder={searchplaceholder || ""}
            value={keyword || ""}
            onChange={(e) => setKeyword(e.target.value.toLowerCase())}
            onKeyDown={(e) => {
              handleKeyDown(e);
            }}
            rightIcon={() => !readOnly && <></>}
          />
        ) : (
          <TextInput
            id={id}
            disabled={disabled}
            name={name}
            ref={ref || buttonRef}
            className={`${
              readOnly
                ? "[&_input]:border-none [&_input]:p-0 [&_input]:!rounded-none  [&_input:focus]:border-none [&_input:focus]:ring-0 [&_input]:text-gray-700"
                : "[&_input]:cursor-pointer [&_input]:text-gray-600"
            } [&_input]:!bg-transparent`}
            onClick={toggleDropdown}
            placeholder={nullLabel || "--Select--"}
            type="text"
            readOnly
            value={
              selectedOption
                ? formatDisplayName
                  ? formatDisplayName(selectedOption)
                  : selectedOption[optionName]
                : readOnly
                  ? "----"
                  : disabled
                    ? nullLabel
                    : ""
            }
            rightIcon={() =>
              !readOnly &&
              !disabled && (
                <svg
                  className={`h-4 w-4 ml-2 transition-transform transform  rotate-180`}
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M4.5 15.75l7.5-7.5 7.5 7.5"
                  />
                </svg>
              )
            }
          />
        )}
      </div>
      
      <ul
        ref={dropdownRef}
        className={`${positionBelow ? "" : "bottom-[48px]"} 
         absolute mt-2 bg-white border border-gray-200 w-auto p-3 max-h-[250px] overflow-y-auto 
            rounded-md bg-white leading-6 shadow-lg ring-1 ring-gray-900/5         transition-all ${
              isOpen
                ? "ease-out opacity-100  z-40 translate-y-0 duration-250"
                : "ease-in opacity-0 -z-40 translate-y-1 duration-100"
            } `}
        role="listbox"
        tabIndex="-1"
        aria-labelledby="dropdown-label"
        style={{ width: dropdownWidth ? `${dropdownWidth}px` : "135px" }}
      >
        {showNull && (
          <li
            key="showNull"
            ref={!selectedOption ? selectedRef : null}
            className={`${
              (focusedIndex === 0 && formikValue && "bg-gray-200") || ""
            } cursor-pointer px-2 py-1 rounded hover:bg-gray-200 ${
              !formikValue ? "bg-blue-600 bg-opacity-20 " : ""
            }`}
            onClick={() => handleOptionClick(null)}
            onMouseEnter={(e) => {
              startTransition(() => setFocusedIndex(0));
            }}
          >
            {nullLabel || "None"}
          </li>
        )}
        {_options
          ?.filter((option) =>
            remote
              ? true
              : !keyword
                ? true
                : option[optionName]
                    ?.toString()
                    .toLowerCase()
                    .includes(keyword) ||
                  option[optionId]
                    .toString()
                    .toLowerCase()
                    .includes(keyword.toLowerCase())
          )
          .map((option, index) => (
            <li
              key={option[optionId]}
              id={option[optionId]}
              ref={
                selectedOption && selectedOption[optionId] === option[optionId]
                  ? selectedRef
                  : null
              }
              className={`${
                (focusedIndex === (showNull ? index + 1 : index) &&
                  option[optionId] !== formikValue &&
                  "bg-gray-200") ||
                ""
              } flex flex-nowrap mt-1 items-center gap-x-4 cursor-pointer px-2 py-1 rounded ${
                option.option_disabled
                  ? "bg-gray-100 text-gray-400 disabled-option"
                  : option[optionId] === formikValue
                    ? "bg-blue-600 bg-opacity-20 "
                    : ""
              }`}
              role="option"
              aria-selected={option[optionId] === selectedOption?.value}
              onMouseEnter={(e) => {
                startTransition(() =>
                  setFocusedIndex(showNull ? index + 1 : index)
                );
              }}
              onClick={(e) =>
                !option.option_disabled && handleOptionClick(option, e.target)
              }
            >
              {optionPrefix && optionPrefix(option)}
              <p>
                {formatDisplayName
                  ? formatDisplayName(option)
                  : option[optionName]}
              </p>
            </li>
          ))}
      </ul>
      {showError && (formikTouched ? formikTouched : false) && (
        <p className="first-letter:uppercase mt-2 text-sm text-red-500">
          {formikError ? formikError : ""}
        </p>
      )}
    </div>
  );
}

export default forwardRef(SearchDropdown);
